import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Clock, Calendar, BookOpen, CheckCircle, GraduationCap, FileText, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Layout from '../components/layout/Layout';
import { departments, programmes, applicationFormUrl } from '../data/mock';

const getProgramme = (programmeId) => {
  for (let i = 0; i < programmes.length; i++) {
    if (programmes[i].id === programmeId) return programmes[i];
  }
  return null;
};

const getDepartment = (deptId) => {
  for (let i = 0; i < departments.length; i++) {
    if (departments[i].id === deptId) return departments[i];
  }
  return null;
};

const getRelatedProgrammes = (deptId, currentId) => {
  const related = [];
  for (let i = 0; i < programmes.length; i++) {
    if (programmes[i].departmentId === deptId && programmes[i].id !== currentId && related.length < 3) {
      related.push(programmes[i]);
    }
  }
  return related;
};

const ProgrammeDetail = () => {
  const { id } = useParams();
  const programme = getProgramme(id);
  const department = programme ? getDepartment(programme.departmentId) : null;

  if (!programme) {
    return (
      <Layout>
        <div className="container-custom py-20 text-center">
          <h1 className="font-display text-3xl font-bold text-[#1e3a5f] mb-4">Programme Not Found</h1>
          <Link to="/programmes">
            <Button>Back to Programmes</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${department?.image})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <Link
            to="/programmes"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Programmes
          </Link>
          <Badge className="bg-[#d4a855] text-[#142942] font-medium mb-4">
            {department?.shortName}
          </Badge>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
            {programme.name}
          </h1>
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 text-white/80">
              <BookOpen className="w-5 h-5 text-[#d4a855]" />
              <span>NTA Level {programme.ntaLevels.join('-')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-5 h-5 text-[#d4a855]" />
              <span>{programme.duration}</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Calendar className="w-5 h-5 text-[#d4a855]" />
              <span>{programme.intakes.join(' & ')} Intake</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold">
                Apply Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a5f]">
                <Send className="mr-2 w-4 h-4" />
                Request Information
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Programme Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full justify-start bg-[#f8fafc] p-1 mb-6">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="requirements" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                    Requirements
                  </TabsTrigger>
                  <TabsTrigger value="structure" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                    Structure
                  </TabsTrigger>
                  <TabsTrigger value="careers" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
                    Careers
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <div className="prose max-w-none">
                    <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-4">Programme Overview</h2>
                    <p className="text-[#475569] leading-relaxed text-lg">
                      {programme.overview}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="requirements">
                  <div className="prose max-w-none">
                    <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-4">Entry Requirements</h2>
                    <p className="text-[#475569] leading-relaxed">
                      {programme.entryRequirements}
                    </p>
                    <div className="mt-6 bg-[#f8fafc] rounded-lg p-6">
                      <h3 className="font-semibold text-[#1e3a5f] mb-3">General Requirements:</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-[#475569]">
                          <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                          <span>Form IV Certificate (CSEE) with required passes</span>
                        </li>
                        <li className="flex items-start gap-2 text-[#475569]">
                          <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                          <span>National ID or valid passport</span>
                        </li>
                        <li className="flex items-start gap-2 text-[#475569]">
                          <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                          <span>Medical fitness certificate</span>
                        </li>
                        <li className="flex items-start gap-2 text-[#475569]">
                          <CheckCircle className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                          <span>Two passport-size photographs</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="structure">
                  <div className="prose max-w-none">
                    <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-4">Programme Structure</h2>
                    <p className="text-[#475569] leading-relaxed mb-6">
                      {programme.programmeStructure}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {programme.ntaLevels.map((level) => (
                        <Card key={level} className="border border-[#e2e8f0]">
                          <CardContent className="pt-4 text-center">
                            <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-3">
                              <span className="text-[#d4a855] font-bold">{level}</span>
                            </div>
                            <h4 className="font-semibold text-[#1e3a5f]">NTA Level {level}</h4>
                            <p className="text-sm text-[#475569]">Year {level - 3}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="careers">
                  <div className="prose max-w-none">
                    <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-4">Career Pathways</h2>
                    <p className="text-[#475569] mb-6">
                      Graduates of this programme can pursue various career opportunities including:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {programme.careerPathways.map((career, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 bg-[#f8fafc] p-4 rounded-lg"
                        >
                          <GraduationCap className="w-6 h-6 text-[#d4a855] flex-shrink-0" />
                          <span className="text-[#1e3a5f] font-medium">{career}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Info Card */}
                <Card className="border-none shadow-lg">
                  <CardHeader className="bg-[#1e3a5f] text-white rounded-t-lg">
                    <CardTitle className="text-lg">Programme Details</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex justify-between py-2 border-b border-[#e2e8f0]">
                      <span className="text-[#475569]">Department</span>
                      <span className="font-medium text-[#1e3a5f]">{department?.shortName}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#e2e8f0]">
                      <span className="text-[#475569]">Duration</span>
                      <span className="font-medium text-[#1e3a5f]">{programme.duration}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#e2e8f0]">
                      <span className="text-[#475569]">NTA Levels</span>
                      <span className="font-medium text-[#1e3a5f]">{programme.ntaLevels.join(', ')}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-[#475569]">Intakes</span>
                      <div className="flex gap-2">
                        {programme.intakes.map((intake) => (
                          <Badge key={intake} className="bg-[#d4a855] text-[#142942] text-xs">
                            {intake}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Intake Notice */}
                {programme.intakes.length === 1 && programme.intakes[0] === 'September' && (
                  <Card className="border-[#d4a855] bg-[#fef3c7]">
                    <CardContent className="pt-4">
                      <p className="text-sm text-[#92400e]">
                        <strong>Note:</strong> This programme is available only during the September intake.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* CTA Card */}
                <Card className="border-none shadow-lg bg-[#f8fafc]">
                  <CardContent className="pt-6">
                    <FileText className="w-10 h-10 text-[#1e3a5f] mb-4" />
                    <h4 className="font-display font-semibold text-[#1e3a5f] mb-2">
                      Ready to Apply?
                    </h4>
                    <p className="text-[#475569] text-sm mb-4">
                      Start your application today and take the first step towards your future.
                    </p>
                    <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer" className="block">
                      <Button className="w-full bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
                        Start Application
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Programmes */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-8 text-center">
            Other Programmes in {department?.shortName}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {getRelatedProgrammes(programme.departmentId, programme.id).map((relatedProgramme) => (
                <Link key={relatedProgramme.id} to={`/programmes/${relatedProgramme.id}`}>
                  <Card className="h-full card-hover border-none shadow-md cursor-pointer">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-display text-[#1e3a5f] text-lg">
                        {relatedProgramme.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-[#475569] text-sm line-clamp-2 mb-3">
                        {relatedProgramme.overview}
                      </p>
                      <div className="flex items-center text-[#2563eb] font-medium text-sm">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProgrammeDetail;
